import { Button, IconButton, TextareaAutosize, TextField } from "@mui/material"
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "elements/Box";
import { ProjectConfig } from 'Global';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import EventIcon from '@mui/icons-material/Event';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TopBar from "elements/TopBar/TopBar";
import Toolbar from "elements/Toolbar/Toolbar";
import BasicAccordion from "elements/BasicAccordion";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import BasicAccordionPdf from 'elements/BasicAccordionPdf';
import { getSantoralLabel } from 'hooks/useLabels';
import { formatSantoralIdentity } from 'utils/santoralCategory';

import { Viewer } from '@react-pdf-viewer/core'; // install this library
// Plugins
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout'; // install this library
// Import the styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
// Worker
import { Worker } from '@react-pdf-viewer/core';

export default function SantoralViewer(props: any) {


  /*=======================================
    GET PROJECT ID AND ESTIMATE ID FROM URL
  =======================================*/
  let { santoral_id } = useParams();
  /*=======================================
  =======================================*/

  const lang_id = localStorage.getItem('lang_id') || '1';

  const defaultValues: any = {
    santoral_id: santoral_id ? santoral_id : "null",
    image: "",
    name: "",
    date: null,
    bioHTML: "",
    bioJSON: null,
    translations: {} as Record<string, any>,
  };

  const [values, setValues] = useState(defaultValues)
  interface pdfData {
    file_id: any;
    file: any;
    name: string;
    estado: string;
  }

  const [filesPdf, setFilesPdf] = useState<pdfData[]>([]);

  /*=======================================
    GET DETAILS IF EDITING
  =======================================*/
  useEffect(() => {
    props.setLoading(true);
    if (santoral_id) {
      axios
        .get(ProjectConfig.api_url, {
          params: {
            request: "santoral_details",
            id: santoral_id,
            lang_id: lang_id,
          },
        })
        .then((response) => {
          // console.log("response")
          // console.log(response)
          setValues(response.data);
          props.setLoading(false);

          setFilesPdf(response.data.files)
        })
        .catch((err) => {
          console.log(err.data);
        });
    }
  }, []);
  /*=======================================
  =======================================*/
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Verificar si el dispositivo es móvil o tablet
    const mediaQuery = window.matchMedia('(max-width: 768px)');

    function handleDeviceChange(e: any) {
      setIsMobile(e.matches);
    }

    // Registrar el oyente para cambios en la consulta de medios
    mediaQuery.addEventListener('change', handleDeviceChange);

    // Establecer el estado inicial
    handleDeviceChange(mediaQuery);

    // Desregistrar el oyente al desmontar el componente
    return () => {
      mediaQuery.removeEventListener('change', handleDeviceChange);
    };
  }, []);

  function download(fileUrl: any, fileName: any) {
    //add extennsion to file name
    fileName = fileName;
    // DOWNLOAD REMOTE FILE from url
    axios({
      url: fileUrl,
      method: 'GET',
      responseType: 'blob', // important
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    }
    );

  }

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  // Funciones helper para obtener contenido según idioma
  const getName = () => {
    if (lang_id === '1') return values.name;
    return values.translations?.[lang_id]?.name || '';
  };

  const getBioHTML = (field: string) => {
    if (lang_id === '1') return values[field];
    return values.translations?.[lang_id]?.[field] || '';
  };

  const hasNativeSourceMarker = (html: unknown) =>
    /\\bdata-desideria-native-source=(["'])[^"']+\\1/i.test(
      String(html || '')
    );

  const inicial_html = "<p class=\"PlaygroundEditorTheme__paragraph\"><span></span></p>";

  // El indicador tienePropio es metadato editorial, no una garantía de que
  // todos los campos litúrgicos estén vacíos o llenos. La vista debe depender
  // del contenido real para no ocultar textos legítimos ni mostrar acordeones
  // vacíos.
  const hasBioContent = (field: string) => {
    const html = getBioHTML(field);
    if (html == null) return false;
    const normalized = String(html).trim();
    if (normalized === "" || normalized === inicial_html) return false;

    // Nunca exponer placeholders editoriales heredados como contenido
    // litúrgico real. Se conservan en base hasta que una fuente certificada
    // permita reemplazarlos.
    const plainText = normalized
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

    return plainText !== "prueba" && plainText !== "test";
  };

  const liturgicalFields = [
    'bioHTML_primeras_visperas',
    'bioHTML_oficio_lectura',
    'bioHTML_laudes',
    'bioHTML_horas_intermedias',
    'bioHTML_visperas',
    'bioHTML_completas',
    'bioHTML_lecturas',
  ];

  // Regla permanente: ningún perfil, incluido HSF, puede fabricar
  // contenido litúrgico a partir de referencias, rúbricas o instrucciones.
  // Solo se renderiza contenido real almacenado. En idiomas no ES, además,
  // el bloque debe acreditar una fuente nativa mediante el marcador.
  const getLiturgicalContent = (
    field: string,
    _referenceField?: string
  ) => {
    if (lang_id === '1') {
      return hasBioContent(field) ? getBioHTML(field) : '';
    }

    const localized = getBioHTML(field);
    return (
      hasBioContent(field)
      && hasNativeSourceMarker(localized)
    )
      ? localized
      : '';
  };

  const hasVerifiedLocalizedLiturgy =
    lang_id === '1'
      ? liturgicalFields.some(hasBioContent)
      : liturgicalFields.some(
          (field) =>
            hasBioContent(field)
            && hasNativeSourceMarker(getBioHTML(field))
        );

  const hasLiturgicalContent = hasVerifiedLocalizedLiturgy;

  // "Calendario propio HSF" debe mostrar únicamente contenido propio de la
  // Congregación. La sincronización canónica añadió temporalmente un bloque
  // técnico del Calendario Romano/Colombia dentro de este campo; se excluye
  // de la vista sin tocar ningún contenido editorial HSF que sí exista.
  const getCalendarHsfHTML = () => {
    const html = getBioHTML('bioHTML_calendario');
    if (html == null) return "";

    return String(html)
      .replace(
        /<p\s+data-desideria-canonical-meta=["']v1["'][^>]*>.*?<\/p>/gis,
        ""
      )
      .trim();
  };

  return (
    <div id="SantoralView" className="module_container">

      <div className="module_topBar" style={{ padding: 0 }}>
        <TopBar title={getSantoralLabel('santoral_titulo')} />
        <Toolbar />
      </div>

      <div className='module_contentSantoral'>
        <div id='mv_image' style={{ padding: 20 }}>
          <img src={values.image} />
        </div>


        <h2>
          <strong>{formatSantoralIdentity(getName(), values.categoria)}</strong>
        </h2>

        {/* <div id="HTMLContent">
          <div dangerouslySetInnerHTML={{ __html: values.firstParagraph }} />
        </div> */}

        <br />
        {(getBioHTML('bioHTML') != "" && getBioHTML('bioHTML') != null && getBioHTML('bioHTML') != inicial_html) && (
          <>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <BasicAccordion titulo={getSantoralLabel('biografia')} contenido={getBioHTML('bioHTML')} />
            </div>
          </>
        )}
        {(getBioHTML('bioHTML_oraciones') != "" && getBioHTML('bioHTML_oraciones') != null && getBioHTML('bioHTML_oraciones') != inicial_html) && (
          <>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <BasicAccordion titulo={getSantoralLabel('oraciones_del_santo')} contenido={getBioHTML('bioHTML_oraciones')} />
            </div>
          </>
        )}
        {(getCalendarHsfHTML() != "" && getCalendarHsfHTML() != inicial_html) && (
          <>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <BasicAccordion titulo={getSantoralLabel('calendario_propio_hsf')} contenido={getCalendarHsfHTML()} />
            </div>
          </>
        )}


        {/* <div id='mv_date'>
          <h3>Fecha del santo</h3>
          <span>{values.date}</span>
        </div>

        <h3>Biografía</h3>
        <div id="HTMLContent">
          <div dangerouslySetInnerHTML={{ __html: values.bioHTML }} />
        </div> */}



        {hasLiturgicalContent && (
          <>

            {getLiturgicalContent('bioHTML_primeras_visperas', 'primeras_visperas') !== '' && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <BasicAccordion titulo={getSantoralLabel('primeras_visperas')} contenido={getLiturgicalContent('bioHTML_primeras_visperas', 'primeras_visperas')} />
              </div>
            )}

            {getLiturgicalContent('bioHTML_oficio_lectura', 'oficio_lectura') !== '' && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <BasicAccordion titulo={getSantoralLabel('oficio_lectura')} contenido={getLiturgicalContent('bioHTML_oficio_lectura', 'oficio_lectura')} />
              </div>
            )}

            {getLiturgicalContent('bioHTML_laudes', 'laudes') !== '' && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <BasicAccordion titulo={getSantoralLabel('laudes')} contenido={getLiturgicalContent('bioHTML_laudes', 'laudes')} />
              </div>
            )}

            {getLiturgicalContent('bioHTML_horas_intermedias', 'horas_intermedias') !== '' && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <BasicAccordion titulo={getSantoralLabel('horas_intermedias')} contenido={getLiturgicalContent('bioHTML_horas_intermedias', 'horas_intermedias')} />
              </div>
            )}

            {getLiturgicalContent('bioHTML_visperas', 'visperas') !== '' && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <BasicAccordion titulo={getSantoralLabel('visperas')} contenido={getLiturgicalContent('bioHTML_visperas', 'visperas')} />
              </div>
            )}

            {getLiturgicalContent('bioHTML_completas', 'completas') !== '' && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <BasicAccordion titulo={getSantoralLabel('completas')} contenido={getLiturgicalContent('bioHTML_completas', 'completas')} />
              </div>
            )}

            {getLiturgicalContent('bioHTML_lecturas', 'lecturas') !== '' && (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <BasicAccordion titulo={getSantoralLabel('lecturas')} contenido={getLiturgicalContent('bioHTML_lecturas', 'lecturas')} />
              </div>
            )}
          </>
        )}

{
        filesPdf.map((file,index) => (

          <BasicAccordionPdf titulo={file.name} filePdf={file.file}></BasicAccordionPdf>
        ))

      }

      </div>

    </div>

  );
};


